/* raw-stand.js - The Raw Comics Stand: the UI (builder U, work order U, 2026-10-07; Jake M-092..M-096).
   DESIGN NOTE
   - What: a port of the accepted prototype (artifact Version 18, ~/AI/MT4/profile/prototypes/stand/) into the theme:
     the stand from above (top view), the book viewer, the Review box, Save | Cart, keys, swipes, the sorting /
     publisher sections / balanced boxes / divider rules (bv-data.js) and the ?q= search with wall tabs. '76 Booth look only.
   - Standards: ~/AI/FLEET/standards/S-build.md 1b (usability first, then quality, then tokens), 2b/2c (simplest safe
     path: no CDN libraries, no build step, one classic script), 3d/3e (small single-job sections, explicit interfaces,
     one writer per file: window.RawStandData + window.RawStandCart are builder D's, used exactly as STAND-CONTRACT.md
     and their own headers define them), 4c (measured, not claimed: preview/stand-harness.html).
     ~/AI/FLEET/standards/S-principles.md: least work for the person served (the shopper), quality as a human sees it.
   - Plan "Keeping it fast and easy" (Jake M-095): no images on the first screen; covers only inside the viewer, for the
     current book +/- 2, from Shopify's CDN with &width= and a 1x/2x srcset; the first data page draws at once and the
     rest re-flows in behind it; JS+CSS <= 100 KB gzipped with builder D's two files.
   - Native replacements: Swiper -> a scroll-snap gallery; GSAP -> the Web Animations API; tinykeys -> one keydown
     handler; @use-gesture -> pointer events. Dropped: three.js / the 3D table, lite mode, the design and colour pickers,
     the on-page error box, the dev/test hooks, the simulated cart drawer (Cart opens the store's cart), the stand's own
     search box (the theme header searches; the stand reads ?q=).
   - Kept storage keys: rc3d-review-v1 (Review box), rc3d-enter-target (Save | Cart), rc3d-guide-seen (first-run card).
   - Scope: every node lives inside the mount (.raw-stand); the dialogs are appended there too (showModal lifts them into
     the top layer), so raw-stand.css scopes every rule under .raw-stand. The only page-level touch is html.rs-lock
     (no page scroll behind the open viewer). */
(function () {
  'use strict';
  var D = document, W = window;
  var mount = D.querySelector('.raw-stand[data-feed]') || D.querySelector('.raw-stand') || D.getElementById('raw-stand');
  if (!mount || mount.getAttribute('data-rs-booted')) return;
  mount.setAttribute('data-rs-booted', '1');

  var CFG = {
    gridUrl: mount.getAttribute('data-grid-url') || '/collections/all',
    cartUrl: mount.getAttribute('data-cart-url') || ((W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/') + 'cart',
    searchUrl: mount.getAttribute('data-search-url') || ((W.Shopify && W.Shopify.routes && W.Shopify.routes.root) || '/') + 'search',
    count: parseInt(mount.getAttribute('data-count'), 10) || 0,
    currency: mount.getAttribute('data-currency') || 'USD'
  };

  /* ================= helpers ================= */
  var reduced = function () { return W.matchMedia('(prefers-reduced-motion: reduce)').matches; };
  var plural = function (n, w) { return n.toLocaleString('en-US') + ' ' + w + (n === 1 ? '' : (/x$/.test(w) ? 'es' : 's')); };
  var EASE_IN = 'cubic-bezier(.55,.085,.68,.53)', EASE_OUT = 'cubic-bezier(.215,.61,.355,1)', EASE_BACK = 'cubic-bezier(.34,1.56,.64,1)';
  function h(tag, attrs) {
    var el = D.createElement(tag), kids = Array.prototype.slice.call(arguments, 2);
    if (attrs) for (var k in attrs) {
      var v = attrs[k];
      if (v == null || v === false) continue;
      if (k === 'class') el.className = v;
      else if (k === 'style') el.style.cssText = v;
      else el.setAttribute(k, v === true ? '' : v);
    }
    (function add(list) {
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (c == null || c === false) continue;
        if (Array.isArray(c)) add(c); else el.append(c.nodeType ? c : String(c));
      }
    })(kids);
    return el;
  }
  var P = {   // SF Symbols-like weight: 1.8-2.2 strokes, rounded caps
    bag: '<path d="M5 8.5h14l-1.3 11a1.5 1.5 0 0 1-1.5 1.3H7.8a1.5 1.5 0 0 1-1.5-1.3z" fill="currentColor"/><path d="M9 8.5V7a3 3 0 0 1 6 0v1.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    bookmark: '<path d="M7 3.8h10a1 1 0 0 1 1 1v15.4l-6-4.3-6 4.3V4.8a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    bookmarkOn: '<path d="M7 3.8h10a1 1 0 0 1 1 1v15.4l-6-4.3-6 4.3V4.8a1 1 0 0 1 1-1z" fill="currentColor" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
    close: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>',
    up: '<path d="M6 14.5l6-6 6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
    down: '<path d="M6 9.5l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
    left: '<path d="M14.5 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
    right: '<path d="M9.5 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
    photos: '<rect x="3.5" y="7" width="13" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M8 4h10a2 2 0 0 1 2 2v10" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    tabs: '<path d="M4 20V9.5a1 1 0 0 1 1-1h4.5l1.5-2.5h8a1 1 0 0 1 1 1V20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M4 20h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
    help: '<circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M9.6 9.4a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .8-1 1.5v.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="16.6" r="1.1" fill="currentColor"/>',
    more: '<circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="1.7"/><circle cx="7.9" cy="12" r="1.35" fill="currentColor"/><circle cx="12" cy="12" r="1.35" fill="currentColor"/><circle cx="16.1" cy="12" r="1.35" fill="currentColor"/>'
  };
  function icon(name, cls) {
    var s = D.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('viewBox', '0 0 24 24'); s.setAttribute('class', cls || 'icon'); s.setAttribute('aria-hidden', 'true');
    s.innerHTML = P[name];
    return s;
  }
  // localStorage can throw (private mode, blocked storage): every access is guarded.
  var store = {
    get: function (k, fb) { try { var v = localStorage.getItem(k); return v == null ? fb : JSON.parse(v); } catch (e) { return fb; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* memory only */ } }
  };
  var isTyping = function (el) { return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)); };
  var moneyFmt;
  try { moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: CFG.currency }); } catch (e) { moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }); }
  var money = function (n) { return moneyFmt.format(n); };
  // Shopify's image CDN sizes the file (and serves WebP/AVIF where the browser takes it).
  function cdn(url, w) {
    if (!url) return '';
    url = String(url).replace(/([?&])width=\d+(&|$)/, function (m, a, b) { return b ? a : ''; });
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + Math.round(w);
  }
  function setSrc(img, url, w) {      // 1x / 2x from the CDN; srcset first so only one file is fetched
    if (!url) return false;
    img.srcset = cdn(url, w) + ' 1x, ' + cdn(url, Math.min(w * 2, 2400)) + ' 2x';
    img.src = cdn(url, w);
    return true;
  }
  function anim(el, frames, opts) { return el && el.animate && !reduced() ? el.animate(frames, opts) : null; }

  /* ================= data: sort, sections, boxes, dividers, search (prototype bv-data.js) ================= */
  var BOX_SIZE = 140;
  var collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  var escRe = function (s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
  // Series key + issue number, for the "alphabetical and numeric" sort.
  function parseTitle(t) {
    var k = t.lastIndexOf('#');
    var series = (k > 0 ? t.slice(0, k) : t).trim();
    var issue = k > 0 ? t.slice(k + 1).trim() : '';
    var seriesKey = series.toLowerCase().replace(/^the\s+/, '').replace(/\s+/g, ' ');
    var num, suf, m = issue.match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/);
    if (m) { num = parseFloat(m[1]); suf = m[2].toLowerCase(); }
    else if (!issue) { num = -1e6; suf = ''; }          // one-shots with no number go first
    else { num = 1e6; suf = issue.toLowerCase(); }      // "Annual", "King-Size" style issues go last
    return { series: series, seriesKey: seriesKey, num: num, suf: suf };
  }
  function compareBooks(a, b) {
    return collator.compare(a._sk, b._sk) || (a._num - b._num) || collator.compare(a._suf, b._suf) ||
      collator.compare(a.t, b.t) || collator.compare(String(a.id), String(b.id));
  }
  function gradeOf(d) {
    var m = d.match(/\b(CGC|CBCS|PGX)\s+(?:(Universal|Restored|Qualified|Signature Series)\s+)?(\d{1,2}(?:\.\d)?)\b/i);
    if (m) return { value: m[3], kind: m[1].toUpperCase() + (m[2] && /restored/i.test(m[2]) ? ' restored' : ''), slab: true };
    m = d.match(/Grade\s+([^\n]+?)\s*[—–]\s*raw/i);
    if (m) return { value: m[1].trim(), kind: 'Raw', slab: false };
    return null;
  }
  var pubOf = function (b) { return b.v === 'EC' ? 'E.C.' : b.v || 'Unknown publisher'; };
  var sKey = function (b) { return pubOf(b) + '|' + b._sk; };
  var slug = function (n) { return n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); };
  var isWall = function (b) { return b.tags.indexOf('wall') >= 0; };

  var BOOKS = [], BY_ID = new Map(), BY_VID = new Map(), PUB = new Map(), SERIES = new Map();
  function prepBook(b) {
    if (b._sk === undefined) {
      var p = parseTitle(String(b.t || ''));
      b.t = String(b.t || '');
      b.tags = Array.isArray(b.tags) ? b.tags : [];
      b._series = p.series; b._sk = p.seriesKey; b._num = p.num; b._suf = p.suf;
    }
    b.d = b.d || '';
    b.a = b.avail !== false && b.a !== false;
    var off = +b.off || 0, shelf = parseFloat(b.p) || 0;
    b._was = off ? shelf : 0;                                   // shelf price before the store's automatic discount
    b._price = Math.round(shelf * (100 - off)) / 100;           // what the shopper pays
    b._search = (b.t + ' ' + b.d + ' ' + (b.v || '') + ' ' + (b.s || '')).toLowerCase();
    return b;
  }
  function setLibrary(raw) {
    BOOKS = raw.map(prepBook).sort(compareBooks);
    BY_ID = new Map(); BY_VID = new Map(); PUB = new Map(); SERIES = new Map();
    BOOKS.forEach(function (b) {
      BY_ID.set(String(b.id), b);
      if (b.vid != null) BY_VID.set(String(b.vid), b);
      if (!isWall(b)) { PUB.set(pubOf(b), (PUB.get(pubOf(b)) || 0) + 1); SERIES.set(sKey(b), (SERIES.get(sKey(b)) || 0) + 1); }
    });
  }
  var byId = function (id) { return BY_ID.get(String(id)); };

  // One divider per series in the box, at the series' first book.
  function dividersOf(books) {
    var out = [];
    books.forEach(function (b, i) { if (!i || books[i - 1]._sk !== b._sk) out.push({ series: b._series, index: i }); });
    return out;
  }
  // Box label: "Avengers #1-#57" for one series, "Amazing Spider-Man - Avengers" for several.
  function labelOf(bb) {
    var a = bb[0], z = bb[bb.length - 1], iss = function (b) { var k = b.t.lastIndexOf('#'); return k > 0 ? b.t.slice(k) : ''; };
    return a._sk === z._sk ? (a._series + ' ' + iss(a) + (iss(z) && iss(z) !== iss(a) ? '-' + iss(z) : '')).trim() : a._series + ' - ' + z._series;
  }
  // Boxes are balanced (about 90-140 each, no tiny leftover box, an even count so the two-deep rows pair up).
  function balance(sorted) {
    if (!sorted.length) return [];
    var count = Math.ceil(sorted.length / BOX_SIZE);
    if (count > 1 && sorted.length - (count - 1) * BOX_SIZE <= 10) count--;
    if (count % 2 && Math.floor(sorted.length / (count + 1)) >= 90) count++;
    var base = Math.floor(sorted.length / count), extra = sorted.length % count, out = [];
    for (var i = 0, off = 0; i < count; i++) { var size = base + (i < extra ? 1 : 0); out.push(sorted.slice(off, off + size)); off += size; }
    return out;
  }
  // Publisher stand (Jake M-076): Wall Books first in their own boxes with no dividers; then one section per publisher
  // (Marvel, DC, then A-Z); publishers under 90 books share "Mixed publishers". Divider tabs only for series with 10+
  // books. Section rules count the whole library, so a search keeps them. Searching: wall books get series tabs too
  // (Jake M-091 "when u serch smm tho wall books get tabs").
  function shelvesFor(sorted, searching) {
    var wall = [], groups = new Map(), mixed = [];
    sorted.forEach(function (b) {
      if (isWall(b)) { wall.push(b); return; }
      if ((PUB.get(pubOf(b)) || 0) < 90) { mixed.push(b); return; }
      if (!groups.has(pubOf(b))) groups.set(pubOf(b), []);
      groups.get(pubOf(b)).push(b);
    });
    var rank = function (n) { return n === 'Marvel' ? 0 : n === 'DC' ? 1 : 2; };
    var secs = [];
    if (wall.length) secs.push({ key: 'wall', name: 'Wall Books', books: wall, wall: true });
    Array.from(groups.keys()).sort(function (a, b) { return rank(a) - rank(b) || a.localeCompare(b); })
      .forEach(function (n) { secs.push({ key: slug(n), name: n, books: groups.get(n) }); });
    if (mixed.length) secs.push({ key: 'mixed', name: 'Mixed publishers', books: mixed.sort(function (a, b) { return pubOf(a).localeCompare(pubOf(b)) || compareBooks(a, b); }) });
    return secs.map(function (sec) {
      sec.wallTabs = !!(sec.wall && searching);
      sec.boxes = balance(sec.books).map(function (bb, k) {
        return { n: k + 1, books: bb, label: labelOf(bb),
          dividers: sec.wall ? (searching ? dividersOf(bb) : []) : dividersOf(bb).filter(function (d) { return (SERIES.get(sKey(bb[d.index])) || 0) >= 10; }) };
      });
      return sec;
    });
  }
  function search(q) {
    var words = String(q).toLowerCase().split(/\s+/).map(function (w) { return w.replace(/^#/, ''); }).filter(Boolean);
    if (!words.length) return [];
    return BOOKS.filter(function (b) { return words.every(function (w) { return b._search.indexOf(w) >= 0; }); });   // BOOKS is pre-sorted
  }

  /* ================= lists: Review box (localStorage), the real cart, the Enter switch, toast ================= */
  var KEY = { review: 'rc3d-review-v1', target: 'rc3d-enter-target', guide: 'rc3d-guide-seen' };
  var L = { review: [], target: 'review', cartCount: 0, inCart: new Set(), busy: new Set() };
  var reviewCtx = false;
  var reviewListeners = [];
  var RSC = function () { return W.RawStandCart || null; };

  var hasReview = function (id) { return L.review.indexOf(String(id)) >= 0; };
  var inCart = function (b) { return !!b && b.vid != null && L.inCart.has(String(b.vid)); };
  // Saved ids are kept even when their book is not loaded yet (the list streams in); only shown books count.
  var reviewBooks = function () { return L.review.map(byId).filter(Boolean); };
  // Saved by the prototype (variant ids): move each to its product id once its book has loaded. Unknown ids are kept.
  function migrateReview() {
    var changed = false;
    L.review = L.review.map(function (id) {
      if (BY_ID.has(id) || !BY_VID.has(id)) return id;
      changed = true; return String(BY_VID.get(id).id);
    }).filter(function (id, k, all) { return all.indexOf(id) === k; });
    if (changed) saveReview();
  }

  function loadLists() {
    var v = store.get(KEY.review, []);
    L.review = (Array.isArray(v) ? v : []).map(String);
    L.target = store.get(KEY.target, null) === 'cart' ? 'cart' : 'review';
  }
  function saveReview() { store.set(KEY.review, L.review); }
  function cartState(count, vids) {
    if (typeof count === 'number') L.cartCount = count;
    if (vids && typeof vids.forEach === 'function') { var s = new Set(); vids.forEach(function (x) { s.add(String(x)); }); L.inCart = s; }
    sync();
  }
  function initCart() {
    var c = RSC();
    if (!c) return;
    if (typeof c.onChange === 'function') c.onChange(function (count, info) { cartState(count, info && info.vids); });
    refreshCart();
  }
  function refreshCart() {          // on boot and on pageshow (Back from the cart page: the badge must be right)
    var c = RSC();
    if (!c) return;
    var p = typeof c.state === 'function' ? c.state() : typeof c.count === 'function' ? c.count().then(function (n) { return { count: n }; }) : null;
    if (p && p.then) p.then(function (s) { cartState(s && s.count, s && s.vids); }, function () { /* the badge stays as the theme rendered it */ });
  }

  function addReview(b) {
    if (!b) return false;
    if (inCart(b)) { toast('Already in your cart: ' + b.t); return false; }
    if (hasReview(b.id)) { toast('Already in your Review box: ' + b.t); return false; }
    L.review.push(String(b.id)); saveReview(); sync('review');
    toast('Saved to the Review box: ' + b.t);
    return true;
  }
  function removeReview(id, quiet) {
    if (!hasReview(id)) return false;
    L.review = L.review.filter(function (x) { return x !== String(id); });
    saveReview(); sync('review');
    if (!quiet) { var b = byId(id); toast('Removed from the Review box: ' + (b ? b.t : 'book')); }
    return true;
  }
  // The real cart (builder D's RawStandCart). A book moves from "maybe" (Review) to the cart on success.
  function addCart(b) {
    if (!b) return Promise.resolve(false);
    var id = String(b.id);
    if (!b.a) { toast('Sold out: ' + b.t + " can't go in the cart"); return Promise.resolve(false); }
    if (inCart(b)) { toast('Already in your cart: ' + b.t); if (hasReview(id)) removeReview(id, true); return Promise.resolve(false); }
    if (L.busy.has(id)) return Promise.resolve(false);
    var c = RSC();
    if (!c || typeof c.add !== 'function') { toast("The cart isn't available right now. Please reload the page."); return Promise.resolve(false); }
    L.busy.add(id); sync();
    return Promise.resolve().then(function () { return c.add(b); }).then(function (res) {
      L.busy.delete(id);
      if (b.vid != null) L.inCart.add(String(b.vid));
      if (res && typeof res.count === 'number') L.cartCount = res.count;
      if (hasReview(id)) { L.review = L.review.filter(function (x) { return x !== id; }); saveReview(); }
      sync('cart');
      toast('Added to cart: ' + b.t);
      return true;
    }, function (err) {
      L.busy.delete(id); sync();
      var msg = err && err.message ? String(err.message) : '';
      var say = function () { toast("Couldn't add " + b.t + ' to the cart' + (msg && msg.length < 140 ? ': ' + msg : '. Please try again.'), true); };
      if (!(err && err.status === 422 && typeof c.state === 'function')) { say(); return false; }
      return c.state().then(function (st) {          // Shopify refused it: usually the one copy is already in the cart
        cartState(st && st.count, st && st.vids);
        if (inCart(b)) { if (hasReview(id)) removeReview(id, true); toast('Already in your cart: ' + b.t); } else say();
        return false;
      }, function () { say(); return false; });
    });
  }
  function addTo(list, b) { return list === 'cart' ? addCart(b) : Promise.resolve(addReview(b)); }
  // Enter: the Save | Cart target; in the Review box Enter always sends the book to the cart.
  function enter(b) {
    if (!b) return;
    if (!reviewCtx) { addTo(L.target, b); return; }
    if (inCart(b)) { if (hasReview(b.id)) removeReview(b.id, true); toast('Already in your cart: ' + b.t); return; }
    addCart(b);
  }
  function setTarget(t) {
    if (t !== 'cart' && t !== 'review') return;
    L.target = t; store.set(KEY.target, t); syncTarget();
    if (S.table) S.table.setEnterLabel(t === 'cart' ? 'Enter adds to cart' : 'Enter saves');
  }

  // Cart / Save buttons (viewer); in the Review box Save becomes Remove.
  function actionButtons(b, compact, review) {
    var btns = (review ? ['cart'] : ['cart', 'review']).map(function (list) {
      var btn = h('button', { class: 'act act-' + list, type: 'button', 'data-list-add': list, 'data-id': b.id, 'data-compact': compact ? '1' : null },
        icon(list === 'cart' ? 'bag' : 'bookmark'), h('span', { class: 'act-label' }));
      syncButton(btn);
      return btn;
    });
    if (review) btns.push(h('button', { class: 'act act-remove', type: 'button', 'data-review-remove': b.id, 'aria-label': 'Remove ' + b.t + ' from your Review box' },
      icon('close'), h('span', { class: 'act-label' }, compact ? 'Remove' : 'Remove from Review box')));
    return btns;
  }
  function syncButton(btn) {
    var b = byId(btn.getAttribute('data-id'));
    if (!b) return;
    var list = btn.getAttribute('data-list-add'), c = btn.getAttribute('data-compact'), label, aria, on;
    if (list === 'cart') {
      var busy = L.busy.has(String(b.id));
      on = inCart(b);
      btn.disabled = !b.a || busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      label = !b.a ? 'Sold out' : busy ? 'Adding…' : on ? (c ? 'In cart' : 'In cart ✓') : (c ? 'Cart' : 'Add to cart');
      aria = !b.a ? b.t + ' is sold out' : busy ? 'Adding ' + b.t + ' to the cart' : on ? b.t + ' is in your cart. Open the cart' : 'Add ' + b.t + ' to cart, ' + money(b._price);
    } else {
      on = hasReview(b.id);
      label = on ? (c ? 'Saved' : 'Saved ✓') : (c ? 'Save' : 'Save to Review box');
      aria = on ? b.t + ' is in your Review box. Open the Review box' : 'Save ' + b.t + ' to your Review box';
      btn.querySelector('svg').innerHTML = P[on ? 'bookmarkOn' : 'bookmark'];
    }
    btn.setAttribute('data-state', on ? 'in' : 'out');
    btn.querySelector('.act-label').textContent = label;
    btn.setAttribute('aria-label', aria);
  }
  function sync(bump) {
    mount.querySelectorAll('[data-list-add]').forEach(syncButton);
    var nReview = reviewBooks().length;
    mount.querySelectorAll('[data-cart-pill]').forEach(function (p) {
      p.querySelector('.pill-count').textContent = L.cartCount;
      p.setAttribute('aria-label', 'Cart: ' + plural(L.cartCount, 'item') + '. Open the cart');
      if (bump === 'cart') anim(p, [{ transform: 'scale(1.25) rotate(-4deg)' }, { transform: 'none' }], { duration: 450, easing: EASE_BACK });
    });
    D.querySelectorAll('[data-open-review]').forEach(function (p) {
      var c = p.querySelector('.pill-count, [data-review-count]');
      c.textContent = nReview; if (c.hasAttribute('data-review-count')) c.hidden = !nReview;
      p.setAttribute('aria-label', 'Review box: ' + plural(nReview, 'book') + '. Open the Review box');
      if (bump === 'review' || bump === 'cart') anim(p, [{ transform: 'scale(1.25) rotate(-4deg)' }, { transform: 'none' }], { duration: 450, easing: EASE_BACK });
    });
    if (bump) reviewListeners.forEach(function (fn) { fn(); });
  }
  function enterSwitch(review) {
    if (review) return h('p', { class: 'review-enter-hint' }, h('kbd', null, 'Enter'), h('span', null, ' adds this book to your cart here'));
    var b = h('button', { class: 'enter-switch', type: 'button', role: 'switch', 'data-enter-switch': '' },
      h('span', { class: 'sw-label' }, 'Enter adds to'),
      h('span', { class: 'sw-track', 'aria-hidden': 'true' }, h('span', { class: 'sw-opt sw-save' }, 'Save'), h('span', { class: 'sw-opt sw-cart' }, 'Cart'), h('span', { class: 'sw-knob' })));
    paintSwitch(b); return b;
  }
  function paintSwitch(b) {
    var cart = L.target === 'cart';
    b.setAttribute('aria-checked', String(cart)); b.setAttribute('data-target', L.target);
    b.setAttribute('aria-label', cart ? 'Enter adds books to your cart. Switch to saving them to the Review box' : 'Enter saves books to your Review box. Switch to adding them to the cart');
  }
  function syncTarget() { mount.querySelectorAll('[data-enter-switch]').forEach(paintSwitch); }

  // Toast: a manual popover so it shows above an open dialog.
  var toastEl, toastTimer = 0;
  function toast(msg, isError) {
    toastEl.textContent = msg;
    toastEl.classList.toggle('is-error', !!isError);
    try { if (toastEl.matches(':popover-open')) toastEl.hidePopover(); toastEl.showPopover(); } catch (e) { toastEl.classList.add('is-shown'); }
    anim(toastEl, [{ transform: 'translateY(20px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 220, easing: EASE_BACK });
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      var a = anim(toastEl, [{ opacity: 1 }, { transform: 'translateY(10px)', opacity: 0 }], { duration: 200, fill: 'forwards' });
      var hide = function () { try { toastEl.hidePopover(); } catch (e) { toastEl.classList.remove('is-shown'); } if (a) a.cancel(); };
      if (a) a.onfinish = hide; else hide();
    }, isError ? 4200 : 2200);
  }

  /* ================= the book viewer (native <dialog>) ================= */
  var V = { open: false, intro: false, shelf: null, box: null, books: [], i: 0, el: null, w: 600, cb: {}, anims: [], gal: null, ret: null };
  var inReview = function () { return !!(V.shelf && V.shelf.key === 'review'); };
  var vClosing = null;
  var dlg, booksEl, posN, posT, posLive, boxName, closeLabel, closeBtn, tabsBtn, tabsPanel, prevBtn, nextBtn, vMain;
  var compactMQ = W.matchMedia('(max-width: 759px)');

  function cartPill(label) {
    return h('a', { class: 'pill pill-cart', href: CFG.cartUrl, 'data-cart-pill': '' }, icon('bag'), h('span', { class: 'pill-label' }, label), h('span', { class: 'pill-count' }, '0'));
  }
  function reviewPill(label) {
    return h('button', { class: 'pill pill-review', type: 'button', 'data-open-review': '', 'aria-haspopup': 'dialog' }, icon('bookmark'), h('span', { class: 'pill-label' }, label), h('span', { class: 'pill-count' }, '0'));
  }

  function initViewer() {
    tabsBtn = h('button', { class: 'tabs-btn', type: 'button', 'data-tabs': '', 'aria-expanded': 'false', 'aria-controls': 'v-tabs', 'aria-keyshortcuts': 'T' }, icon('tabs'), h('span', null, 'Tabs'));
    tabsPanel = h('div', { class: 'v-tabs', id: 'v-tabs', role: 'list', 'aria-label': 'Series in this box', 'data-keep-focus': '', hidden: true });
    posN = h('span', { class: 'v-pos-n' }, '1'); posT = h('span', { class: 'v-pos-t' }, '1'); posLive = h('span', { class: 'sr-only' });
    boxName = h('p', { class: 'v-boxname', id: 'v-boxname' });
    prevBtn = h('button', { class: 'v-arrow', type: 'button', 'data-step': '-1', 'aria-label': 'Previous book', 'aria-keyshortcuts': 'ArrowUp W' }, icon('up'));
    nextBtn = h('button', { class: 'v-arrow', type: 'button', 'data-step': '1', 'aria-label': 'Next book', 'aria-keyshortcuts': 'ArrowDown S Space' }, icon('down'));
    booksEl = h('div', { class: 'v-books' });
    closeLabel = h('span', { class: 'v-close-label' }, 'Box View');
    closeBtn = h('button', { class: 'v-close', type: 'button', 'data-close-viewer': '', 'aria-label': 'Close the book, back to the stand', 'aria-keyshortcuts': 'Escape' }, icon('left'), closeLabel);
    vMain = h('div', { class: 'v-main', tabindex: '-1', 'aria-label': 'Book' }, booksEl);
    dlg = h('dialog', { class: 'rs-viewer', id: 'rs-viewer', 'aria-labelledby': 'v-boxname', 'aria-describedby': 'v-pos' },
      h('div', { class: 'v-bar' },
        closeBtn,
        h('div', { class: 'v-where' }, boxName, h('p', { class: 'v-pos', id: 'v-pos', 'aria-live': 'polite', 'aria-atomic': 'true' }, posN, h('span', { class: 'v-pos-sep' }, ' / '), posT, posLive)),
        tabsBtn,
        h('div', { class: 'v-pills' }, cartPill('Cart'), reviewPill('Review')),
        h('button', { class: 'help-btn', type: 'button', 'data-open-guide': '', 'aria-label': 'Keyboard and swipe guide', 'aria-keyshortcuts': 'Shift+?' }, icon('help')),
        h('span', { class: 'v-break', 'aria-hidden': 'true' }),
        h('nav', { class: 'v-nav', 'aria-label': 'Flip books' }, prevBtn, nextBtn)),
      tabsPanel, vMain);
    mount.append(dlg);

    dlg.addEventListener('cancel', function (e) {   // Esc: closes Tabs first if they are open, otherwise the book
      e.preventDefault();
      if (!tabsPanel.hidden) { toggleTabs(false); tabsBtn.focus({ preventScroll: true }); return; }
      userClose();
    });
    // The browser can close a modal dialog by itself (repeated Esc): treat it as the user closing the book.
    dlg.addEventListener('close', function () {   // (the event is async: ignore it when the viewer already reopened)
      if (!V.open || dlg.open) return;
      V.open = false; reviewCtx = false; vClosing = null;
      afterClose();
      if (V.cb.onClose) V.cb.onClose();
    });
    compactMQ.addEventListener('change', function () { if (V.open) render(V.i, 0); });
    initSwipes();
  }

  function openViewer(o) {
    V.intro = false; V.shelf = o.shelf; V.box = o.box; V.books = o.box.books;
    V.cb = { onNextBox: o.onNextBox, onClose: o.onClose, next: o.next || null, onIndex: o.onIndex };
    reviewCtx = inReview();
    V.w = coverWidth();
    boxName.textContent = inReview() ? o.shelf.name : o.shelf.name + ' · Box ' + o.box.n;
    closeLabel.textContent = inReview() ? 'Back' : 'Box View';
    closeBtn.setAttribute('aria-label', inReview() ? 'Close the Review box' : 'Close the book, back to the stand');
    posT.textContent = o.box.books.length.toLocaleString('en-US');
    toggleTabs(false);
    tabsBtn.hidden = !(o.box.dividers || []).length;
    var first = !V.open;
    if (vClosing) { var c = vClosing; vClosing = null; if (c.anim) c.anim.cancel(); c.resolve(); }   // reopened mid-fade
    if (!dlg.open) { V.ret = D.activeElement; dlg.showModal(); }
    mount.classList.add('is-viewing'); D.documentElement.classList.add('rs-lock');
    V.open = true;
    V.i = -1;
    go(Math.max(0, Math.min(o.index || 0, V.books.length)), 0);
    vMain.focus({ preventScroll: true });
    if (first) anim(dlg, [{ opacity: 0 }, { opacity: 1 }], { duration: 250 });
  }
  function afterClose() {
    mount.classList.remove('is-viewing'); D.documentElement.classList.remove('rs-lock');
    booksEl.replaceChildren(); V.el = null; V.gal = null;
    var t = V.ret; V.ret = null;
    if (!(t && t.isConnected && mount.contains(t) && !dlg.contains(t))) t = mount.querySelector('.tv-box.is-current');
    if (t && t.focus) t.focus({ preventScroll: true });
  }
  function closeViewer() {
    if (!V.open) return vClosing ? vClosing.promise : Promise.resolve();
    V.open = false; reviewCtx = false;
    finishAnims();
    var resolve, promise = new Promise(function (r) { resolve = r; });
    var c = vClosing = { promise: promise, resolve: resolve, anim: null };
    var finish = function () {
      if (vClosing !== c) return;               // reopened meanwhile
      vClosing = null;
      if (dlg.open) dlg.close();
      if (c.anim) c.anim.cancel();
      afterClose();
      resolve();
    };
    c.anim = anim(dlg, [{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' });
    if (c.anim) c.anim.onfinish = finish; else finish();
    return promise;
  }
  function userClose() { var cb = V.cb.onClose; closeViewer(); if (cb) cb(); }

  var viewerState = function () { return { open: V.open, shelf: V.shelf && V.shelf.key, box: V.box && V.box.n, index: V.i }; };
  var current = function () { return V.open && !V.intro && V.i >= 0 && V.i < V.books.length ? V.books[V.i] : null; };
  var viewerContext = function () { return V.shelf ? { shelf: V.shelf, box: V.box, index: Math.max(0, V.i), onNextBox: V.cb.onNextBox, onClose: V.cb.onClose, next: V.cb.next, onIndex: V.cb.onIndex } : null; };
  // Review box: the saved list changed. Show the book now at `index` (the next remaining one), or the end card.
  function replaceBooks(books, index) {
    if (!V.open) return;
    if (books.length === V.books.length && books.every(function (b, k) { return String(b.id) === String(V.books[k].id); })) return;
    V.books = books; V.box = Object.assign({}, V.box, { books: books });
    posT.textContent = books.length.toLocaleString('en-US');
    V.intro = false; V.i = -1;
    go(Math.max(0, Math.min(index, books.length)), 1);
  }
  function step(d) { if (V.open) { if (V.intro) { if (d > 0) go(V.i, 1); } else go(V.i + d, d); } }
  // In-box Tabs (Jake M-053): the box's series dividers as a list; tap one to jump to its first book.
  function toggleTabs(show) {
    if (show === undefined) show = tabsPanel.hidden;
    if (show && !(V.box && (V.box.dividers || []).length)) return;    // the Review box has no series tabs
    if (show) {
      var cur = V.books[V.i] ? V.books[V.i]._series : '';
      var all = V.box.dividers;
      tabsPanel.replaceChildren.apply(tabsPanel, all.map(function (d, k) {
        var n = (k + 1 < all.length ? all[k + 1].index : V.books.length) - d.index;
        return h('button', { class: 'v-tab', type: 'button', role: 'listitem', 'data-tab-index': d.index, 'aria-current': String(d.series === cur) },
          h('span', { class: 'v-tab-name' }, d.series), h('span', { class: 'v-tab-n' }, String(n)));
      }));
    }
    tabsPanel.hidden = !show; tabsBtn.setAttribute('aria-expanded', String(show));
    if (show) { var f = tabsPanel.querySelector('[aria-current="true"]') || tabsPanel.firstChild; if (f) f.focus({ preventScroll: false }); }
  }
  function goTo(i) { if (V.open) go(i, Math.sign(i - V.i)); }

  function go(i, dir) {
    var n = V.books.length;
    i = Math.max(0, Math.min(i, n));
    if (i === V.i && !V.intro) return;
    if (V.intro) { V.intro = false; store.set(KEY.guide, true); }
    V.i = i;
    render(i, dir);
    var b = V.books[i];
    posN.textContent = i === n ? 'End' : (i + 1).toLocaleString('en-US');
    posLive.textContent = b ? ': ' + b.t + ', ' + money(b._price) : inReview() ? (n ? ': end of your review box' : ': your review box is empty') : ': end of ' + V.shelf.name + ' Box ' + V.box.n;
    prevBtn.disabled = i === 0;
    nextBtn.disabled = i === n;
    preload(i);
    wantDetail(b);
    if (V.cb.onIndex) V.cb.onIndex(i);
  }

  /* ---------- one book (or the end card) with the flip ---------- */
  function finishAnims() { var l = V.anims; V.anims = []; l.forEach(function (a) { try { a.finish(); } catch (e) { /* already gone */ } }); }
  function render(i, dir) {
    finishAnims();
    var old = V.el;
    var el = i === V.books.length ? endCard() : bookView(V.books[i]);
    V.el = el;
    booksEl.append(el);
    initGallery(el);
    // End of a box: focus the next-box button. Not in Review: a second Enter there must not close the viewer.
    if (i === V.books.length && !inReview()) { var nb = el.querySelector('[data-next-box], [data-close-viewer].btn-cta'); if (nb) nb.focus({ preventScroll: true }); }
    if (!old) return;
    old.classList.add('is-leaving'); old.inert = true;
    old.querySelectorAll('[id]').forEach(function (e) { e.removeAttribute('id'); });
    var done = function () { old.remove(); };
    if (!dir || reduced() || !el.animate) { done(); return; }
    // Pull the current comic up and back out of the box; the next one rises into place.
    var up = dir > 0, list = [];
    var oBag = old.querySelector('.bag') || old, nBag = el.querySelector('.bag') || el;
    var notStage = function (c) { return !c.classList.contains('v-stage'); };
    var oInfo = Array.prototype.filter.call(old.children, notStage), nInfo = Array.prototype.filter.call(el.children, notStage);
    list.push(oBag.animate([{ transform: 'none', opacity: 1 }, { transform: 'translateY(' + (up ? -38 : 34) + '%) rotateX(' + (up ? 28 : -24) + 'deg) scale(.92)', opacity: 0 }], { duration: 300, easing: EASE_IN, fill: 'forwards' }));
    list.push(nBag.animate([{ transform: 'translateY(' + (up ? 26 : -26) + '%) rotateX(' + (up ? -16 : 16) + 'deg) scale(.95)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 360, delay: 100, easing: EASE_OUT, fill: 'backwards' }));
    oInfo.forEach(function (c) { list.push(c.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: 'forwards' })); });
    nInfo.forEach(function (c) { list.push(c.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, delay: 160, fill: 'backwards' })); });
    V.anims = list;
    Promise.all(list.map(function (a) { return a.finished; })).then(done, done);
  }

  var TAGS = { gold: 'Golden Age', silver: 'Silver Age', bronze: 'Bronze Age', key: 'Key issue', wall: 'Wall book', slab: 'Slabbed', 'just-in': 'Just in' };
  var issueOf = function (b) { var k = b.t.lastIndexOf('#'); return k > 0 ? b.t.slice(k).trim() : ''; };
  var wasOf = function (b) { return b._was > b._price ? b._was : 0; };
  var yearOf = function (b) { var m = String(b.d || '').split(/\n/)[0].match(/\b(19[3-9]\d|20[0-3]\d)\b/); return m ? m[1] : ''; };
  function metaEl(b) {   // issue / year line under the title
    var bits = [issueOf(b) ? 'Issue ' + issueOf(b) : '', yearOf(b)].filter(Boolean);
    return bits.length ? h('p', { class: 'x-meta' }, bits.join(' · ')) : null;
  }
  function priceEl(b) {   // original price struck through before the sale price (Jake M-050)
    return h('p', { class: 'x-price' }, h('span', { class: 'sr-only' }, 'Price '),
      wasOf(b) ? h('s', { class: 'x-was', 'aria-label': 'was ' + money(wasOf(b)) }, money(wasOf(b))) : null,
      h('span', { class: 'x-now' }, money(b._price)),
      b.off ? h('span', { class: 'x-off' }, '-' + b.off + '%') : null,
      b.a ? null : h('span', { class: 'x-out' }, 'Sold out'));
  }
  // Jake M-151 (Bruce): show the grade under the title + issue ONLY when the book has one. Never guessed.
  function bookGrade(b) {
    var g = b.g ? String(b.g).trim() : '';
    if (g) return gradeOf(g) || { value: g.replace(/^grade[:\s]*/i, ''), kind: 'Grade', slab: /\b(CGC|CBCS|PGX)\b/i.test(g) };
    return gradeOf(b.d || '');
  }
  function gradeEl(b) {
    var g = bookGrade(b);
    if (!g || !g.value) return null;
    return h('span', { class: 'x-grade' + (g.slab ? ' is-slab' : ''), role: 'img', 'aria-label': 'Grade ' + g.value + ', ' + g.kind },
      h('span', { class: 'x-grade-kind' }, g.kind), h('span', { class: 'x-grade-val' }, g.value));
  }
  function gradeRow(b) { var g = gradeEl(b); return g ? h('div', { class: 'x-graderow' }, g) : null; }
  function tagsEl(b) {
    var t = Object.keys(TAGS).filter(function (k) { return b.tags.indexOf(k) >= 0; });
    return h('p', { class: 'x-tags' }, b.v ? h('span', { class: 'x-tag x-tag-pub' }, b.v) : null, t.map(function (k) { return h('span', { class: 'x-tag x-tag-' + k }, TAGS[k]); }));
  }
  function descEl(b) {
    var paras = (b.d || '').split(/\n+/).map(function (s) { return s.trim(); }).filter(Boolean);
    return h('div', { class: 'x-desc', id: 'v-desc' }, paras.length ? paras.map(function (p) { return h('p', null, p); }) : h('p', null, b._detail ? 'No description yet.' : 'Loading the details…'));
  }
  // The side rail beside the cover everywhere; phones get a narrow rail (Jake, owner source 17620: info beside the image).
  function bookView(b) {
    var ctx = { compact: compactMQ.matches, review: inReview() };
    var photos = b.imgs && b.imgs.length > 1 ? b.imgs : null;
    var bag = h('div', { class: 'bag' },
      h('div', { class: 'bag-flap' }),
      h('div', { class: 'bag-cover' }, photos ? gallery(b, photos) : coverImg(b, 0)),
      h('span', { class: 'bag-wait', 'aria-hidden': 'true' }, 'Pulling it from the box…'));
    var rail = h('aside', { class: 'x-rail', 'aria-label': 'Book details' },
      h('h2', { class: 'x-title', id: 'v-book-title' }, h('span', { class: 'x-series' }, b._series), issueOf(b) ? h('span', { class: 'x-issue' }, ' ' + issueOf(b)) : null),
      metaEl(b), gradeRow(b), h('div', { class: 'x-row' }, priceEl(b)),
      h('div', { class: 'x-acts' }, actionButtons(b, ctx.compact, ctx.review)), enterSwitch(ctx.review), tagsEl(b), descEl(b));
    return h('article', { class: 'v-book info-1' + (ctx.compact ? ' is-compact' : '') + (ctx.review ? ' is-review' : ''), 'aria-labelledby': 'v-book-title', 'data-book': b.id },
      h('div', { class: 'v-stage' }, bag), photos ? photosBar(photos) : null, rail);
  }
  // Details arrived (description, all photos, live price): update the open book in place, keeping focus.
  function patchBook(b) {
    var el = V.el;
    if (!el || el.getAttribute('data-book') !== String(b.id)) return;
    var rail = el.querySelector('.x-rail'), t = rail.querySelector('.x-title');
    var oldMeta = rail.querySelector('.x-meta'), m = metaEl(b);
    if (oldMeta) oldMeta.remove();
    if (m) t.after(m);
    var oldG = rail.querySelector('.x-graderow'), gr = gradeRow(b);
    if (oldG) oldG.remove();
    if (gr) (m || t).after(gr);
    var row = rail.querySelector('.x-row');
    row.replaceChildren(priceEl(b));
    rail.querySelector('.x-desc').replaceWith(descEl(b));
    var photos = b.imgs && b.imgs.length > 1 ? b.imgs : null;
    if (photos && !el.querySelector('.v-gallery')) {
      el.querySelector('.bag-cover').replaceChildren(gallery(b, photos));
      el.querySelector('.v-stage').after(photosBar(photos));
      initGallery(el);
    }
    sync();
    posLive.textContent = ': ' + b.t + ', ' + money(b._price);
  }
  var detailTimer = 0;
  function wantDetail(b) {
    clearTimeout(detailTimer);
    var RSD = W.RawStandData;
    if (!b || b._detail || !RSD || typeof RSD.detail !== 'function') return;
    detailTimer = setTimeout(function () {         // a short wait: fast flipping does not fetch every book
      RSD.detail(b).then(function (r) {
        if (r && r !== b) { if (r.d != null) b.d = r.d; if (Array.isArray(r.imgs)) b.imgs = r.imgs; ['p', 'vid', 'avail', 'a', 'c'].forEach(function (k) { if (r[k] !== undefined) b[k] = r[k]; }); }
        b._detail = true; prepBook(b);
        if (V.open && !V.intro && V.books[V.i] === b) patchBook(b);
      }, function () {
        if (V.open && !V.intro && V.books[V.i] === b && V.el) {
          var d = V.el.querySelector('.x-desc');
          if (d) d.replaceChildren(h('p', null, 'The details could not load. Check your connection.'));
        }
      });
    }, 120);
  }

  function coverImg(b, k) {
    var url = k && b.imgs && b.imgs[k] ? b.imgs[k] : b.img;
    var img = h('img', { class: 'cover', alt: k ? b.t + ', photo ' + (k + 1) : b.t, decoding: 'async', loading: k > 1 ? 'lazy' : null, draggable: 'false' });
    var fail = function () { var bag = img.closest('.bag'), w = bag && bag.querySelector('.bag-wait'); if (w) w.textContent = 'Photo coming soon'; };
    img.addEventListener('load', function () {
      var nw = img.naturalWidth, nh = img.naturalHeight, bag = img.closest('.bag');
      if (!nw || !nh || !bag) return;
      if (k === 0) bag.style.setProperty('--ar', Math.max(0.2, Math.min(5, nw / nh)).toFixed(4));
      bag.classList.add('is-loaded');
    });
    img.addEventListener('error', fail, { once: true });
    if (!setSrc(img, url, V.w)) setTimeout(fail, 0);
    return img;
  }
  // Wall-book photos: a native scroll-snap strip (swipe, More pictures, dots, ← →), rewinding at the ends.
  function gallery(b, photos) {
    return h('div', { class: 'v-gallery', id: 'v-gallery', role: 'region', 'aria-roledescription': 'carousel', 'aria-label': b.t + ': ' + photos.length + ' photos' },
      photos.map(function (_, k) { return h('div', { class: 'v-slide', role: 'group', 'aria-roledescription': 'slide', 'aria-label': 'Photo ' + (k + 1) + ' of ' + photos.length }, coverImg(b, k)); }));
  }
  function photosBar(photos) {
    return h('div', { class: 'v-photos' },
      h('button', { class: 'more-photos', type: 'button', 'data-more-photos': '', 'aria-keyshortcuts': 'ArrowRight D', 'aria-controls': 'v-gallery' }, icon('photos'), h('span', null, 'More pictures', h('span', { class: 'mp-n' }, ' (' + photos.length + ')'))),
      h('div', { class: 'v-dots', 'data-keep-focus': '' }, photos.map(function (_, k) {
        return h('button', { class: 'v-dot', type: 'button', 'data-photo': k, 'aria-label': 'Photo ' + (k + 1), 'aria-current': k ? 'false' : 'true' });
      })));
  }
  function initGallery(el) {
    var g = el.querySelector('.v-gallery');
    V.gal = null;
    if (!g) return;
    var dots = Array.prototype.slice.call(el.querySelectorAll('.v-dot'));
    var G = V.gal = { g: g, n: dots.length, i: 0, dots: dots }, raf = 0;
    g.addEventListener('scroll', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () { markPhoto(G, Math.round(g.scrollLeft / Math.max(1, g.clientWidth))); });
    }, { passive: true });
  }
  function markPhoto(G, i) { G.i = i; G.dots.forEach(function (d, k) { d.setAttribute('aria-current', String(k === i)); }); }
  function goPhoto(k) {
    var G = V.gal;
    if (!G) return;
    k = (k % G.n + G.n) % G.n;
    markPhoto(G, k);
    G.g.scrollTo({ left: k * G.g.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
  }
  function photo(d) { if (!V.open || !V.gal) return false; goPhoto(V.gal.i + d); return true; }   // normal books: photo keys do nothing

  /* The first card: a short how-to for the top view (Jake: the instructions card first). */
  var KEYS = function () { return h('span', { class: 'ins-keys' }, Array.prototype.map.call(arguments, function (k) { return h('kbd', null, k); })); };
  var burst = function (name) { return h('span', { class: 'burst', 'aria-hidden': 'true' }, h('span', { class: 'burst-core' }, icon(name))); };
  function reviewEndCard() {
    var n = V.books.length;
    return h('article', { class: 'v-book v-end', 'aria-labelledby': 'v-end-title' },
      h('div', { class: 'end-card' + (n ? '' : ' is-empty') },
        n ? null : burst('bookmark'),
        h('p', { class: 'end-kicker' }, 'Review box'),
        h('h2', { class: 'end-title', id: 'v-end-title' }, n ? 'End of your review box' : 'Your review box is empty'),
        h('p', { class: 'end-text' }, n
          ? plural(n, 'book') + ' still in the Review box. Go back through them, or close it.'
          : 'Hit Save on any book while you flip a box, then come back here to decide. In here, Enter sends the book to your cart.'),
        h('div', { class: 'end-actions' }, n
          ? h('button', { class: 'btn btn-cta btn-big', type: 'button', 'data-goto-index': '0' }, '↑ Back to the first saved book')
          : h('button', { class: 'btn btn-cta btn-big', type: 'button', 'data-close-viewer': '' }, 'Close the Review box')),
        n ? h('div', { class: 'end-more' }, h('button', { class: 'btn-link', type: 'button', 'data-close-viewer': '' }, 'Close the Review box')) : null));
  }
  function endCard() {
    if (inReview()) return reviewEndCard();
    var s = V.shelf, box = V.box, nx = V.cb.next;
    var nextName = nx && (nx.shelf.key === s.key ? 'Box ' + nx.box.n : nx.shelf.name + ' · Box ' + nx.box.n);
    var first = nx && nx.box.books[0], peek = null;
    if (first) {   // a peek at the next box's first book (small)
      var img = h('img', { class: 'end-peek-img', alt: '', decoding: 'async', draggable: 'false' });
      setSrc(img, first.img, 80);
      peek = h('div', { class: 'end-peek' }, h('span', { class: 'end-peek-thumb' }, img),
        h('p', { class: 'end-peek-text' }, h('span', { class: 'end-peek-label' }, 'Up next'), h('span', { class: 'end-peek-name' }, nextName), h('span', { class: 'end-peek-book' }, 'Starts with ' + first.t)));
    }
    return h('article', { class: 'v-book v-end', 'aria-labelledby': 'v-end-title' },
      h('div', { class: 'end-card' },
        h('p', { class: 'end-kicker' }, s.name + ' · Box ' + box.n),
        h('h2', { class: 'end-title', id: 'v-end-title' }, 'End of Box ' + box.n),
        h('p', { class: 'end-text' }, "That's all " + plural(box.books.length, 'book') + ' in this box.' + (nx ? '' : ' That was the last box on the stand.')),
        peek,
        h('div', { class: 'end-actions' }, nx
          ? h('button', { class: 'btn btn-cta btn-big', type: 'button', 'data-next-box': '' }, 'Next box → ' + nextName)
          : h('button', { class: 'btn btn-cta btn-big', type: 'button', 'data-close-viewer': '' }, 'Back to the stand')),
        h('div', { class: 'end-more' },
          h('button', { class: 'btn-link', type: 'button', 'data-goto-index': '0' }, '↑ Back to the first book'),
          nx ? h('button', { class: 'btn-link', type: 'button', 'data-close-viewer': '' }, 'Back to the stand') : null)));
  }

  /* ---------- images: the cover's CSS width for this screen (the 2x file comes from srcset); current +/- 2 only ---------- */
  function coverWidth() {
    var vw = W.innerWidth, vh = W.innerHeight, compact = vw < 760;
    var stageW = compact ? vw * 0.72 : Math.min(960, vw - 132) - 372;
    var stageH = compact ? Math.min((0.77 * vw - 28) * 1.62, vh - 120) : vh - 110;
    var want = Math.max(160, Math.min(stageW, stageH * 0.72));
    var steps = [320, 400, 480, 600, 720, 840, 1000, 1200];
    for (var i = 0; i < steps.length; i++) if (steps[i] >= want) return steps[i];
    return 1200;
  }
  var warm = new Map();
  function preload(i) {
    for (var j = i - 2; j <= i + 2; j++) {
      var b = V.books[j];
      if (!b || j === i || warm.has(b.id + '@' + V.w)) continue;
      var img = new Image();
      img.decoding = 'async';
      setSrc(img, b.img, V.w);
      warm.set(b.id + '@' + V.w, img);
      if (warm.size > 40) warm.delete(warm.keys().next().value);
    }
  }

  /* ================= Review box: the same viewer over the saved books ================= */
  var REVIEW = { key: 'review', name: 'Review box' }, reviewBack = null;
  function openReviewBox(index) {
    var st = viewerState();
    if (st.open && st.shelf === 'review') return;            // already in the Review box
    reviewBack = st.open ? viewerContext() : null;           // remember the box being flipped, to come back to it
    var books = reviewBooks();
    openViewer({ shelf: REVIEW, box: { n: 1, books: books, dividers: [] }, index: Math.max(0, Math.min(index || 0, books.length)), next: null, onNextBox: null, onClose: reviewClosed });
  }
  function reviewClosed() {
    var prev = reviewBack; reviewBack = null;
    if (prev) openViewer(prev);                              // back to the same book in the same box
  }
  reviewListeners.push(function () {                         // added to cart / removed: show the next remaining book
    var st = viewerState();
    if (st.open && st.shelf === 'review') replaceBooks(reviewBooks(), st.index);
  });

  /* ================= keys, swipes, the key guide ================= */
  var guide;
  var otherDialog = function () { return mount.querySelector('dialog[open]:not(.rs-viewer)'); };
  var nativeActivate = function (el) { return el && el.closest && el.closest('button, a[href], summary, [role="button"], [role="switch"], [role="slider"], input'); };
  function onKey(e) {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.isComposing) return;
    var k = e.key;
    if (k === '?') {                                           // the guide, anywhere on the stand page
      if (isTyping(e.target) || otherDialog()) return;
      e.preventDefault(); closeMenu(); showGuide(); return;
    }
    if (!V.open || isTyping(e.target) || otherDialog()) return;
    var key = k.length === 1 ? k.toLowerCase() : k;
    if (e.shiftKey && k.length === 1) return;
    var act = function (fn, activation) {
      if (activation && nativeActivate(e.target)) return;      // Enter/Space on a focused button = press that button
      e.preventDefault(); fn();
    };
    switch (key) {
      case 'ArrowUp': case 'w': act(function () { step(-1); }); break;
      case 'ArrowDown': case 's': act(function () { step(1); }); break;
      case ' ': act(function () { step(1); }, true); break;
      case 'ArrowRight': case 'd': act(function () { photo(1); }); break;
      case 'ArrowLeft': case 'a': act(function () { photo(-1); }); break;
      case 'Enter': act(function () { if (!e.repeat) enter(current()); }, true); break;   // holding Enter adds once
      case 't': act(function () { toggleTabs(); }); break;
    }
  }
  // Phone swipes on the book: up = next, down = previous, left/right = photos (the gallery scrolls natively).
  function initSwipes() {
    var sw = null;
    var SCROLLERS = '.x-desc, .x-rail';
    vMain.addEventListener('pointerdown', function (e) {
      if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) { sw = null; return; }
      sw = { x: e.clientX, y: e.clientY, t: e.timeStamp, target: e.target, type: e.pointerType };
    });
    vMain.addEventListener('pointercancel', function () { sw = null; });
    vMain.addEventListener('pointerup', function (e) {
      var s = sw; sw = null;
      if (!s || !V.open || otherDialog()) return;
      var dx = e.clientX - s.x, dy = e.clientY - s.y, ax = Math.abs(dx), ay = Math.abs(dy), dist = Math.max(ax, ay);
      var v = dist / Math.max(1, e.timeStamp - s.t);
      if (dist < 40 || (v < 0.2 && dist < 120)) return;        // a tap or a slow drag is not a swipe
      var sc = s.target.closest && s.target.closest(SCROLLERS);
      if (sc && ay > ax && sc.scrollHeight > sc.clientHeight + 1) return;   // let long descriptions scroll
      if (s.type === 'mouse' && nativeActivate(s.target)) return;
      if (ay > ax) step(dy < 0 ? 1 : -1);
      else if (!(s.target.closest && s.target.closest('.v-gallery')) || s.type === 'mouse') photo(dx < 0 ? 1 : -1);
    });
  }
  var ROWS = [
    [['↓', 'S', 'Space'], 'Next book', 'Swipe up'],
    [['↑', 'W'], 'Previous book', 'Swipe down'],
    [['→', 'D'], 'Next photo (wall books)', 'Swipe left'],
    [['←', 'A'], 'Previous photo (wall books)', 'Swipe right'],
    [['Enter'], 'Save the book to the Review box (flip the Save | Cart switch to send it to the cart)', 'Tap Save or Cart'],
    [['Enter'], 'In the Review box: send the book to your cart', 'Tap Cart · Remove takes it out'],
    [['T'], 'Tabs: the series in this box', 'Tap Tabs'],
    [['Esc'], 'Close the book, back to the stand', 'Tap the ‹ arrow'],
    [['?'], 'This guide', 'Tap ?']
  ];
  function buildGuide() {
    guide = h('dialog', { class: 'rs-guide', 'aria-labelledby': 'rs-guide-title' },
      h('span', { class: 'grabber', 'aria-hidden': 'true' }),
      h('div', { class: 'guide-head' }, h('h2', { class: 'guide-title', id: 'rs-guide-title' }, 'Keys & swipes'),
        h('button', { class: 'guide-close', type: 'button', 'data-close-guide': '', 'aria-label': 'Close the guide' }, icon('close'))),
      h('table', { class: 'guide-table' },
        h('thead', null, h('tr', null, h('th', { scope: 'col' }, 'Keys'), h('th', { scope: 'col' }, 'Does'), h('th', { scope: 'col', class: 'guide-phone' }, 'Phone'))),
        h('tbody', null, ROWS.map(function (r) {
          return h('tr', null,
            h('td', null, r[0].map(function (k, j) { return [j ? h('span', { class: 'guide-or' }, ' / ') : null, h('kbd', null, k)]; })),
            h('td', null, r[1]), h('td', { class: 'guide-phone' }, r[2]));
        }))),
      h('button', { class: 'btn btn-cta btn-block', type: 'button', 'data-close-guide': '', autofocus: true }, 'Done'));
    guide.addEventListener('click', function (e) {
      var r = guide.getBoundingClientRect();
      if (e.target.closest('[data-close-guide]') || (e.target === guide && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom))) guide.close();
    });
    guide.addEventListener('close', function () { store.set(KEY.guide, true); });
    mount.append(guide);
  }
  function showGuide() {
    if (guide.open) return;
    guide.showModal();
    anim(guide, [{ transform: 'translateY(24px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 280, easing: EASE_OUT });
  }
  // Shown once, the first time someone opens a book; remembered after that.


  /* ================= the stand from above (prototype topview.js) =================
     Each column holds two boxes (front box at the bottom beside the publisher strip, the second box behind it on top);
     columns run sideways and the stand scrolls horizontally (drag, trackpad, wheel, arrow keys, the pan buttons).
     Jake M-171 (sketch shots/m170-sketch.png; "remove the thing with the box pulling up", "like i dont want ppl to have to
     click alot", "on the top of the ui for each box is a button that brings a dropdown", "can we make the tabs thinner by
     having a #. next to each tab so ppl can js select the tab number"): there is no close-up step any more. Each box has
     its name button ON TOP of it; the button drops down "Start at the front" + the box's tabs numbered 1, 2, 3... (top to
     bottom), and a pick opens the viewer at that tab's first book. Inside the box the same tabs are thin numbered strips.
     A click (or Enter) anywhere on the box opens its first book. Two clicks at most to any series. */
  // Tabs (Jake M-121/M-142): one per run of 10+ books of a series, smaller runs share a Mixed tab, wall boxes in chunks of
  // ~10 by position (searching: wall books group by series too, Jake M-091). At most TABLE_MAX per box (merged harder).
  var RUN_MIN = 10, TABLE_MAX = 14, TABLE_CHUNKS = 9;
  function runsOf(books) {
    var out = [];
    books.forEach(function (b, i) {
      var last = out[out.length - 1];
      if (last && last.k === sKey(b)) last.n++; else out.push({ k: sKey(b), start: i, n: 1, names: [b._series] });
    });
    return out;
  }
  function chunksOf(n, max) {                              // wall boxes: even chunks of about 10, by position
    var count = Math.max(1, Math.min(max, Math.round(n / 10))), out = [], base = Math.floor(n / count), extra = n % count;
    for (var i = 0, off = 0; i < count; i++) { var len = base + (i < extra ? 1 : 0); out.push({ start: off, n: len, chunk: true }); off += len; }
    return out;
  }
  function blocksOf(shelf, box, max, chunkMax) {
    var n = box.books.length, out = [];
    if (!n) return out;
    if (!shelf.wall || shelf.wallTabs) {
      var runs = runsOf(box.books);
      for (var min = shelf.wall ? 1 : RUN_MIN; ; min = Math.ceil(min * 1.5)) {   // too many tabs to fit: merge harder
        out = [];
        runs.forEach(function (r) {
          var last = out[out.length - 1], big = r.n >= min;
          if (!big && last && !last.big) { last.n += r.n; last.names = last.names.concat(r.names); }
          else out.push({ start: r.start, n: r.n, names: r.names.slice(), big: big });
        });
        if (out.length <= max || min > n) break;
      }
      if (out.length <= max) return out;
    }
    return chunksOf(n, Math.min(max, chunkMax || max));
  }
  // The box's tabs, numbered from 1 in book order (1 = the front of the box, drawn at the top). The strips and the
  // dropdown both come from this one list, so their numbers always match. No counts or issue ranges (Jake MM-360).
  function tabsOf(shelf, box) {
    var k = 0;
    return blocksOf(shelf, box, TABLE_MAX, TABLE_CHUNKS).map(function (bl, i) {
      var mixed = !bl.chunk && bl.names.length > 1;
      return { n: i + 1, start: bl.start, tone: mixed ? 'm' : k++ % 2 ? 'b' : 'a', names: mixed ? bl.names : null,
        name: bl.chunk ? box.books[bl.start].t : mixed ? 'Mixed' : box.books[bl.start]._series };
    });
  }
  function stripsEl(tabs) {                                // Jake M-171: thin equal strips, "#. name"
    return h('div', { class: 'tv-bands', 'aria-hidden': 'true' }, tabs.map(function (t) {
      return h('span', { class: 'tv-band', 'data-tone': t.tone }, h('span', { class: 'tv-bnum' }, String(t.n)), h('span', { class: 'tv-bname' }, t.name));
    }));
  }
  function createTopView(host, opts) {
    var onPick = opts.onPick;
    host.setAttribute('role', 'region');
    host.setAttribute('aria-label', 'The stand from above. Each column is two boxes, front box at the bottom. Scroll sideways for more publishers.');
    host.setAttribute('aria-describedby', 'tv-hint');
    var scroller = h('div', { class: 'tv' });
    var enterTxt = h('span', { class: 'tv-enter' }, L.target === 'cart' ? 'Enter adds to cart' : 'Enter saves');
    // Jake M-113: no RAW COMICS sign; the band is just the rail for the pan arrows. The hint stays for screen readers only.
    var sign = h('div', { class: 'tv-signband' },
      h('p', { class: 'tv-hint', id: 'tv-hint' }, 'Open a box at its first book, or use the name button on top of a box to pick a tab.'));
    var pan = function (dir) {
      return h('button', { class: 'rs-pan rs-pan-' + (dir < 0 ? 'left' : 'right'), type: 'button', 'data-pan': dir, 'aria-label': 'Pan the stand ' + (dir < 0 ? 'left' : 'right') }, icon(dir < 0 ? 'left' : 'right'));
    };
    // Jake M-171: ONE dropdown for the whole table (so only one is ever open), filled for the box whose button opened it.
    // A manual popover: the top layer, so the table's sideways scroller can't clip it.
    var dd = h('div', { class: 'tv-dd', id: 'tv-dd', role: 'menu', tabindex: '-1', popover: 'manual', hidden: true });
    var ddRec = null;
    host.replaceChildren(sign, scroller, dd, pan(-1), pan(1));

    var boxes = new Map();               // "shelfKey|n" -> { shelf, box, el, btn, unit, well, tabs }
    var current = null;                  // last opened { shelfKey, boxN, index }

    function plateName(shelf) { return shelf.wall ? 'Wall' : shelf.key === 'mixed' ? 'Mixed' : shelf.name; }
    function boxEl(shelf, box, where) {
      var id = shelf.key + '|' + box.n, tabs = tabsOf(shelf, box), first = box.books[0];
      var well = h('div', { class: 'tv-well' }, h('div', { class: 'tv-in' }, stripsEl(tabs)));
      // Mixed boxes: no label on the box (Jake M-089/M-090); the publishers stay in the aria-label.
      var pubs = shelf.wall ? [] : Array.from(new Set(box.books.map(pubOf)));
      var mixed = shelf.key === 'mixed' || pubs.length > 1;
      var plate = plateName(shelf) + ' ' + box.n;
      var btn = h('button', { class: 'tv-plate', type: 'button', 'data-dd': id, 'aria-haspopup': 'menu', 'aria-expanded': 'false', 'aria-controls': 'tv-dd',
        'aria-label': plate + ': choose a tab' }, h('span', { class: 'tv-plate-name' }, plate), h('span', { class: 'tv-plate-caret', 'aria-hidden': 'true' }));
      var el = h('div', { class: 'tv-box' + (mixed ? ' is-mixed' : ''), tabindex: '0', role: 'button', 'data-shelf': shelf.key, 'data-box': box.n,
        'aria-label': plate + ', ' + shelf.name + ' ' + where + ' box' + (mixed ? ', publishers: ' + pubs.join(', ') : '') + '. Opens at its first book' + (first ? ': ' + first.t : '') }, well);
      var unit = h('div', { class: 'tv-unit' }, btn, el);
      boxes.set(id, { shelf: shelf, box: box, el: el, btn: btn, unit: unit, well: well, tabs: tabs });
      return unit;
    }

    // keep = a re-flow while the list streams in: hold the sideways position, the focus and an open dropdown.
    function setShelves(list, keep) {
      var left = keep ? scroller.scrollLeft : 0, a = D.activeElement;
      var focused = keep && a && a.closest && a.closest('.tv-box, .tv-plate');
      var focusKey = focused ? focused.getAttribute('data-dd') || focused.getAttribute('data-shelf') + '|' + focused.getAttribute('data-box') : null;
      var openKey = keep && ddRec ? ddRec.shelf.key + '|' + ddRec.box.n : null, openAt = openKey ? items().indexOf(a) : -1;
      closeDD(false); boxes.clear();
      var secs = list.map(function (shelf) {
        var cols = [];
        for (var i = 0; i < shelf.boxes.length; i += 2) {
          var pair = shelf.boxes.slice(i, i + 2);
          cols.push(h('div', { class: 'tv-col' },
            h('div', { class: 'tv-pair' + (pair.length === 1 ? ' is-single' : '') },
              pair.map(function (b, k) { return boxEl(shelf, b, pair.length === 1 ? 'single' : k ? 'back' : 'front'); })),
            h('div', { class: 'tv-strip', 'aria-hidden': 'true' }, shelf.name)));
        }
        return h('section', { class: 'tv-sec' + (shelf.wall ? ' is-wall' : ''), 'aria-label': shelf.name + ': ' + shelf.books.length.toLocaleString('en-US') + ' books in ' + plural(shelf.boxes.length, 'box') }, cols);
      });
      scroller.replaceChildren.apply(scroller, secs);
      scroller.scrollLeft = left;
      markCurrent();
      var fb = focusKey && boxes.get(focusKey);
      if (fb) (focused.classList.contains('tv-plate') ? fb.btn : fb.el).focus({ preventScroll: true });
      if (openKey && boxes.has(openKey)) openDD(boxes.get(openKey), openAt);
    }

    function pick(shelfKey, boxN, index) {
      closeDD(false);
      current = { shelfKey: shelfKey, boxN: boxN, index: index };
      markCurrent();
      onPick({ shelfKey: shelfKey, boxN: boxN, index: index });
    }
    function markCurrent() {
      host.querySelectorAll('.is-current').forEach(function (e) { e.classList.remove('is-current'); });
      var b = current && boxes.get(current.shelfKey + '|' + current.boxN);
      if (b) b.el.classList.add('is-current');
    }

    /* ---------- the dropdown on top of each box (Jake M-171) ---------- */
    function items() { return Array.prototype.slice.call(dd.querySelectorAll('[role="menuitem"]')); }
    function openDD(b, focusAt) {           // focusAt: the item to focus (-1 = leave focus where it is)
      if (ddRec && ddRec !== b) closeDD(false);
      ddRec = b;
      var cur = current && current.shelfKey === b.shelf.key && current.boxN === b.box.n ? current.index : -1, on = -1;
      b.tabs.forEach(function (t, k) { if (cur >= t.start) on = k; });   // the tab holding the last opened book
      dd.setAttribute('aria-label', plateName(b.shelf) + ' ' + b.box.n + ': tabs');
      dd.replaceChildren.apply(dd, [
        h('button', { class: 'tv-dd-item tv-dd-front', type: 'button', role: 'menuitem', 'data-index': 0, 'aria-label': 'Start at the front: ' + (b.box.books[0] ? b.box.books[0].t : '') },
          h('span', { class: 'tv-dd-num', 'aria-hidden': 'true' }, '▶'), h('span', { class: 'tv-dd-name' }, 'Start at the front'))
      ].concat(b.tabs.map(function (t, k) {
          return h('button', { class: 'tv-dd-item', type: 'button', role: 'menuitem', 'data-index': t.start, 'data-tab': t.n, 'data-tone': t.tone, 'aria-current': k === on ? 'true' : null,
            title: t.names ? t.names.join(', ') : null, 'aria-label': 'Tab ' + t.n + ': ' + t.name + (t.names ? ' (' + t.names.join(', ') + ')' : '') },
            h('span', { class: 'tv-dd-num', 'aria-hidden': 'true' }, String(t.n)), h('span', { class: 'tv-dd-name' }, t.name));
        })));
      dd.hidden = false;
      try { if (!dd.matches(':popover-open')) dd.showPopover(); } catch (e) { /* no popover API: it is a fixed layer */ }
      b.btn.setAttribute('aria-expanded', 'true'); b.unit.classList.add('is-open');
      placeDD();
      var list = items();
      if (ddRec && focusAt >= 0 && list.length) list[Math.min(focusAt, list.length - 1)].focus({ preventScroll: true });
    }
    function closeDD(refocus) {
      var b = ddRec;
      if (!b) return;
      ddRec = null;
      try { if (dd.matches(':popover-open')) dd.hidePopover(); } catch (e) { /* no popover API */ }
      dd.hidden = true; dd.replaceChildren();
      b.btn.setAttribute('aria-expanded', 'false'); b.unit.classList.remove('is-open');
      if (refocus && b.btn.isConnected) b.btn.focus({ preventScroll: true });
    }
    // Under the button, centred on it, kept on screen (phones too); above it when there is more room there.
    function placeDD() {
      if (!ddRec) return;
      var r = ddRec.btn.getBoundingClientRect(), s = scroller.getBoundingClientRect(), vw = D.documentElement.clientWidth, vh = W.innerHeight, m = 8;
      if (!r.width || r.right < s.left + 4 || r.left > s.right - 4) { closeDD(false); return; }   // its box scrolled out of sight
      var w = Math.min(vw - 2 * m, Math.max(230, Math.min(320, ddRec.el.offsetWidth + 40)));
      dd.style.width = w + 'px'; dd.style.maxHeight = 'none';
      var need = dd.scrollHeight, below = vh - r.bottom - m - 4, above = r.top - m - 4, up = need > below && above > below;
      var room = Math.max(120, up ? above : below), hgt = Math.min(need, room);
      dd.style.maxHeight = room + 'px';
      dd.style.left = Math.round(Math.max(m, Math.min(vw - w - m, r.left + r.width / 2 - w / 2))) + 'px';
      dd.style.top = Math.round(up ? Math.max(m, r.top - 4 - hgt) : r.bottom + 4) + 'px';
      dd.classList.toggle('is-up', up);
    }
    var placeRaf = 0;
    var replace = function (e) { if (!ddRec || (e && e.target === dd)) return; cancelAnimationFrame(placeRaf); placeRaf = requestAnimationFrame(placeDD); };
    W.addEventListener('resize', replace);
    W.addEventListener('scroll', replace, true);              // the table's sideways scroll, or the page
    dd.addEventListener('click', function (e) {
      var it = e.target.closest('[data-index]'), b = ddRec;
      if (!it || !b) return;
      closeDD(true);                                          // the button keeps focus, so closing the book comes back to it
      pick(b.shelf.key, b.box.n, +it.getAttribute('data-index'));
    });
    dd.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      var list = items(), k = list.indexOf(D.activeElement), to = -1;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closeDD(true); return; }
      if (e.key === 'ArrowDown') to = (k + 1) % list.length;
      else if (e.key === 'ArrowUp') to = k <= 0 ? list.length - 1 : k - 1;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = list.length - 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') { e.preventDefault(); return; }   // not the stand's pan keys
      if (to >= 0) { e.preventDefault(); list[to].focus({ preventScroll: true }); }
    });
    // Tab out of it, or a click/tap anywhere else, closes it (its own button toggles it instead).
    dd.addEventListener('focusout', function (e) {
      var t = e.relatedTarget;
      if (ddRec && t && !dd.contains(t) && t !== ddRec.btn) closeDD(false);
    });
    D.addEventListener('pointerdown', function (e) {
      if (ddRec && !dd.contains(e.target) && !ddRec.btn.contains(e.target)) closeDD(false);
    }, true);

    // A click on a box opens its first book; a click on its name button opens / closes its dropdown.
    scroller.addEventListener('click', function (e) {
      var pl = e.target.closest('.tv-plate');
      if (pl) {
        var b = boxes.get(pl.getAttribute('data-dd'));
        if (b) { if (ddRec === b) closeDD(false); else openDD(b, 0); }
        return;
      }
      var el = e.target.closest('.tv-box');
      if (el && boxes.has(el.getAttribute('data-shelf') + '|' + el.getAttribute('data-box'))) pick(el.getAttribute('data-shelf'), +el.getAttribute('data-box'), 0);
    });

    // keyboard: Enter/Space on a box opens its first book; arrows move between boxes; ArrowDown/Up on a name button opens it
    scroller.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      var pl = e.target.closest('.tv-plate');
      if (pl && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        var pb = boxes.get(pl.getAttribute('data-dd'));
        if (pb) { e.preventDefault(); openDD(pb, e.key === 'ArrowUp' ? pb.tabs.length : 0); }
        return;
      }
      var el = e.target.closest('.tv-box');
      if (!el) return;
      var onBox = e.target === el, to = null;
      if (onBox && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        if (!e.repeat) pick(el.getAttribute('data-shelf'), +el.getAttribute('data-box'), 0);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {   // same depth (front/back) in the neighbouring column
        var col = el.closest('.tv-col'), cols = Array.prototype.slice.call(scroller.querySelectorAll('.tv-col')), k = cols.indexOf(col);
        var next = cols[k + (e.key === 'ArrowRight' ? 1 : -1)];
        if (next) { var mine = Array.prototype.indexOf.call(col.querySelectorAll('.tv-box'), el), theirs = next.querySelectorAll('.tv-box'); to = theirs[Math.min(mine, theirs.length - 1)]; }
      } else if (onBox && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        var pair = el.closest('.tv-pair').querySelectorAll('.tv-box');          // [front, back]
        to = pair[e.key === 'ArrowUp' ? 1 : 0];
        if (to === el) to = null;
      } else if (onBox && (e.key === 'Home' || e.key === 'End')) {
        var all = scroller.querySelectorAll('.tv-box'); to = e.key === 'Home' ? all[0] : all[all.length - 1];
      }
      if (to) { e.preventDefault(); to.focus({ preventScroll: true }); to.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }); }
    });
    // Arrows with nothing focused on the page (and no dialog/menu open) pan the stand.
    W.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
      var t = e.target;
      if (ddRec || mount.querySelector('dialog[open]') || (t !== D.body && t !== D.documentElement && t !== scroller)) return;
      if (!host.isConnected || host.offsetParent === null) return;
      var r = host.getBoundingClientRect();
      if (r.bottom < 80 || r.top > W.innerHeight - 80) return;                   // the stand is scrolled out of view
      e.preventDefault(); panBy(e.key === 'ArrowRight' ? 1 : -1);
    });

    // sideways scrolling: mouse drag + the vertical wheel (at either end the wheel scrolls the page again)
    var drag = { on: false, x: 0, left: 0, moved: false };
    scroller.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag.on = true; drag.x = e.clientX; drag.left = scroller.scrollLeft; drag.moved = false;
    });
    W.addEventListener('pointermove', function (e) {
      if (!drag.on) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) < 6) return;
      if (!drag.moved) { drag.moved = true; scroller.classList.add('is-dragging'); }
      scroller.scrollLeft = drag.left - dx;
    });
    var onUp = function () { if (!drag.on) return; drag.on = false; scroller.classList.remove('is-dragging'); if (drag.moved) setTimeout(function () { drag.moved = false; }, 0); };
    W.addEventListener('pointerup', onUp);
    W.addEventListener('pointercancel', onUp);
    scroller.addEventListener('click', function (e) { if (drag.moved) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
    scroller.addEventListener('wheel', function (e) {
      if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;          // trackpad sideways / pinch: native
      var box = e.target.closest('.tv-box'), el = box && boxes.get(box.getAttribute('data-shelf') + '|' + box.getAttribute('data-box'));
      el = el && el.well;
      if (el && el.scrollHeight > el.clientHeight + 1) {
        var atTop = el.scrollTop <= 0, atEnd = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        if ((e.deltaY < 0 && !atTop) || (e.deltaY > 0 && !atEnd)) return;          // the box scrolls its own strips first
      }
      var max = scroller.scrollWidth - scroller.clientWidth;
      if ((e.deltaY > 0 && scroller.scrollLeft >= max - 1) || (e.deltaY < 0 && scroller.scrollLeft <= 0)) return;   // the page scrolls on
      e.preventDefault();
      scroller.scrollLeft += e.deltaY * (e.deltaMode === 1 ? 32 : 1);
    }, { passive: false });

    function colStep() {
      var c = scroller.querySelector('.tv-col');
      return c ? c.getBoundingClientRect().width + parseFloat(getComputedStyle(c.parentElement).columnGap || 0) : 200;
    }
    function panBy(cols) {
      var dist = Math.min(Math.abs(cols) * colStep(), scroller.clientWidth * 0.85) * Math.sign(cols);
      scroller.scrollBy({ left: dist, behavior: reduced() ? 'auto' : 'smooth' });
    }
    function showBox(b) {
      var r = b.el.getBoundingClientRect(), s = scroller.getBoundingClientRect();
      if (r.left < s.left || r.right > s.right) scroller.scrollLeft += r.left - s.left - (s.width - r.width) / 2;   // sideways only
    }
    function flyToBox(shelfKey, n) {        // the viewer opened (or moved to the next box): mark that box, bring it into view
      var b = boxes.get(shelfKey + '|' + n);
      closeDD(false);
      if (b) {
        if (!current || current.shelfKey !== shelfKey || current.boxN !== n) { current = { shelfKey: shelfKey, boxN: n, index: 0 }; markCurrent(); }
        showBox(b);
      }
      return Promise.resolve();
    }

    setShelves(opts.shelves);
    return { setShelves: setShelves, panBy: panBy, flyToBox: flyToBox, setEnterLabel: function (t) { enterTxt.textContent = t; } };
  }

  /* ================= the page: bar, More menu, routing, boot ================= */
  var S = { shelves: [], q: '', results: [], done: false, table: null, pushed: false, token: 0, routed: '', pendingRoute: false, raw: null };
  var bar, barTitle, barSub, barActs, host, emptyEl, menu;
  try { S.q = (new URLSearchParams(location.search).get('q') || '').trim(); } catch (e) { S.q = ''; }

  function buildShell() {
    var sw = mount.querySelector('.raw-stand__switch, [data-rs-switch]');   // builder D's Stand | Grid switch joins the bar
    barTitle = h('h1', { class: 'rs-title', tabindex: '-1' }, 'Box View');
    barSub = h('p', { class: 'rs-sub', 'aria-live': 'polite' }, CFG.count ? plural(CFG.count, 'comic') : '');
    var tagline = h('p', { class: 'rs-tagline' }, 'Scroll our inventory like you’re at a convention!');
    barActs = h('div', { class: 'rs-results', hidden: true });
    menu = h('div', { class: 'rs-menu', id: 'rs-menu', popover: '', 'aria-label': 'Options' },
      h('div', { class: 'menu-head' }, h('h2', { class: 'menu-title' }, 'Options'),
        h('button', { class: 'icon-btn menu-x', type: 'button', popovertarget: 'rs-menu', popovertargetaction: 'hide', 'aria-label': 'Close options' }, icon('close'))),
      h('div', { class: 'menu-group' }, h('div', { class: 'menu-row' }, h('span', { class: 'menu-label' }, 'Enter key adds to'), enterSwitch(false))),
      h('div', { class: 'menu-group' }, h('button', { class: 'menu-row menu-btn', type: 'button', 'data-open-guide': '', 'aria-keyshortcuts': 'Shift+?' }, h('span', { class: 'menu-label' }, 'Keyboard & gestures'), h('kbd', null, '?'))),
      h('p', { class: 'menu-help' }, 'The stand from above: each column is two boxes, the front box at the bottom beside the publisher sign. Tap a box to open its first book, or tap the name button on top of a box and pick a numbered tab to start at that series (Jake M-171). Drag, swipe or scroll sideways (← → keys too) to move along the stand.'));
    bar = h('div', { class: 'rs-bar' },
      h('div', { class: 'rs-head' }, barTitle, barSub, tagline),
      barActs,
      sw,
      // Jake M-110: the cart stays in the site header only, and the Review box sits beside it there (wireHeaderReview).
      h('div', { class: 'rs-acts' },
        h('button', { class: 'icon-btn rs-more', type: 'button', popovertarget: 'rs-menu', 'aria-label': 'More: Enter key, help', 'aria-haspopup': 'true' }, icon('more'))));
    host = h('div', { class: 'rs-stand' }, skeleton(), h('p', { class: 'rs-boot', role: 'status' }, 'Setting up the stand…'));
    emptyEl = h('p', { class: 'rs-empty', role: 'status', hidden: true });
    toastEl = h('div', { class: 'rs-toast', popover: 'manual', role: 'status', 'aria-live': 'polite' });
    mount.querySelectorAll('[data-rs-fallback]').forEach(function (n) { n.remove(); });
    Array.prototype.slice.call(mount.children).forEach(function (n) { if (n.tagName !== 'NOSCRIPT' && n !== sw) n.remove(); });
    mount.append(bar, host, emptyEl, menu, toastEl);
    mount.classList.toggle('is-search', !!S.q);
    initViewer(); buildGuide();
  }

  // Before any JSON: empty box outlines (no tabs, no images), as many columns as data-count asks for, up to a screenful.
  function skeleton() {
    var cols = Math.max(2, Math.min(12, Math.ceil((CFG.count || 1400) / 280))), list = [];
    for (var i = 0; i < cols; i++) list.push(h('div', { class: 'tv-col' }, h('div', { class: 'tv-pair' }, h('div', { class: 'tv-box' }), h('div', { class: 'tv-box' })), h('div', { class: 'tv-strip' }, ' ')));
    return h('div', { class: 'tv is-skeleton', 'aria-hidden': 'true' }, h('section', { class: 'tv-sec' }, list));
  }

  // The stand fills the screen under the theme's sticky header.
  function measureTop() {
    var hd = D.querySelector('.site-header, #site-header'), top = 0;
    if (hd) { var pos = getComputedStyle(hd).position; if (pos === 'sticky' || pos === 'fixed') top = hd.getBoundingClientRect().height; }
    mount.style.setProperty('--rs-top', Math.round(top) + 'px');
    return hd;
  }

  function renderBar() {
    var n = S.results.length, boxes = S.shelves.reduce(function (a, s) { return a + s.boxes.length; }, 0);
    var more = S.done ? '' : ' so far…';
    if (!S.q) {
      barTitle.textContent = 'Box View';
      barSub.textContent = plural(n, 'comic') + ' · ' + plural(boxes, 'box') + more;
      return;
    }
    barTitle.replaceChildren(n ? plural(n, 'book') + ' match ' : (S.done ? 'Nothing matches ' : 'Looking for '), h('em', null, '“' + S.q + '”'));
    barSub.textContent = n ? plural(boxes, 'box') + ' on the stand' + more : '';
    if (barActs.hidden) {
      var clear = new URL(location.href); clear.searchParams.delete('q'); clear.hash = '';
      barActs.replaceChildren(
        h('nav', { class: 'rs-seg', 'aria-label': 'Show the results as' },
          h('a', { class: 'seg', href: CFG.searchUrl + '?q=' + encodeURIComponent(S.q) }, 'Grid'),
          h('span', { class: 'seg', 'aria-current': 'page' }, 'Box View')),
        h('a', { class: 'rs-clear', href: clear.pathname + clear.search }, 'Clear search'));
      barActs.hidden = false;
    }
    var empty = !n;
    emptyEl.hidden = !empty;
    if (empty) emptyEl.textContent = S.done ? 'No books match that yet. Try a series like Avengers, a creator like Kirby, or a publisher like E.C.' : 'Looking through the boxes…';
    mount.classList.toggle('is-empty', empty);
  }

  function onPick(p) {
    var shelf = S.shelves.filter(function (s) { return s.key === p.shelfKey; })[0];
    if (!shelf || !shelf.boxes[p.boxN - 1]) return;
    S.pushed = true;
    setHash({ box: p.shelfKey + '-' + p.boxN, i: p.index + 1 }, false);
    route();
  }
  function nextOf(shelf, box) {
    if (box.n < shelf.boxes.length) return { shelf: shelf, box: shelf.boxes[box.n] };
    var k = S.shelves.indexOf(shelf), ns = k >= 0 ? S.shelves[k + 1] : null;
    return ns ? { shelf: ns, box: ns.boxes[0] } : null;
  }
  function viewerOpts(shelf, box, index) {
    return { shelf: shelf, box: box, index: index, next: nextOf(shelf, box), onNextBox: onNextBox, onClose: onClose,
      onIndex: function (i) { var o = { box: shelf.key + '-' + box.n }; if (i) o.i = i + 1; setHash(o, true); } };
  }
  function openBox(shelf, box, index) {
    var t = ++S.token;
    Promise.resolve(V.open ? closeViewer() : null).then(function () {
      return S.table.flyToBox(shelf.key, box.n);
    }).then(function () {
      if (t !== S.token) return;
      openViewer(viewerOpts(shelf, box, index));   // straight to the book: no first-run card (Jake M-106)
    });
  }
  function onNextBox(nx) {
    if (!nx) return;
    var t = ++S.token;
    closeViewer().then(function () {
      setHash({ box: nx.shelf.key + '-' + nx.box.n }, true);
      return S.table.flyToBox(nx.shelf.key, nx.box.n);
    }).then(function () {
      if (t !== S.token) return;
      openViewer(viewerOpts(nx.shelf, nx.box, 0));
    });
  }
  function onClose() {
    S.token++;
    if (S.pushed) { S.pushed = false; history.back(); } else setHash({}, true);
  }

  // Hashes: #box=marvel-3&i=5 (i is 1-based) · #review=1&i=2 = the Review box. ?q= (the page URL) is the search.
  function readHash() {
    var out = {};
    location.hash.replace(/^#/, '').split('&').filter(Boolean).forEach(function (pair) {
      var k = pair.indexOf('='), key = k < 0 ? pair : pair.slice(0, k);
      try { out[key] = k < 0 ? '' : decodeURIComponent(pair.slice(k + 1)); } catch (e) { out[key] = pair.slice(k + 1); }
    });
    return out;
  }
  function setHash(p, replace) {
    var qs = Object.keys(p).map(function (k) { return k + '=' + encodeURIComponent(p[k]); }).join('&');
    var url = location.pathname + location.search + (qs ? '#' + qs : '');
    if (url === location.pathname + location.search + location.hash) return;
    if (replace) history.replaceState(history.state, '', url); else history.pushState(null, '', url);
  }
  function route() {
    S.routed = location.href;
    S.pendingRoute = false;
    var p = readHash();
    if (p.review !== undefined) {
      var ri = Math.max(0, (parseInt(p.i, 10) || 1) - 1), rs = viewerState();
      if (rs.open && rs.shelf === 'review') { if (rs.index !== ri) goTo(ri); return; }
      openReviewBox(ri);
      return;
    }
    if (!p.box) {
      if (V.open) { S.token++; closeViewer(); }
      S.pushed = false;
      return;
    }
    var cut = p.box.lastIndexOf('-');
    var shelf = S.shelves.filter(function (s) { return s.key === p.box.slice(0, cut); })[0];
    var box = shelf && shelf.boxes[(parseInt(p.box.slice(cut + 1), 10) || 1) - 1];
    if (!box) {
      if (!S.done) { S.pendingRoute = true; return; }      // the list is still streaming in: try again on the next page
      setHash({}, true); if (V.open) closeViewer(); return;
    }
    var index = Math.max(0, Math.min((parseInt(p.i, 10) || 1) - 1, box.books.length));
    var st = viewerState();
    if (st.open && st.shelf === shelf.key && st.box === box.n) { if (st.index !== index) goTo(index); return; }
    openBox(shelf, box, index);
  }

  function apply(raw) {
    S.raw = raw;
    setLibrary(raw);
    migrateReview();
    S.results = S.q ? search(S.q) : BOOKS;
    S.shelves = shelvesFor(S.results, !!S.q);
    if (!S.table) {
      S.table = createTopView(host, { shelves: S.shelves, onPick: onPick });
      var onNav = function () { if (location.href !== S.routed) route(); };
      W.addEventListener('popstate', onNav);
      W.addEventListener('hashchange', onNav);
      renderBar(); sync();
      route();
      return;
    }
    S.table.setShelves(S.shelves, true);
    renderBar(); sync();
    if (S.pendingRoute) route();
  }
  function fail(err) {
    var msg = err && err.message ? String(err.message) : '';
    host.replaceChildren(h('div', { class: 'rs-fail', role: 'alert' },
      h('p', null, 'The stand could not load.' + (msg ? ' ' + msg : '')),
      h('a', { class: 'btn btn-cta', href: CFG.gridUrl }, 'See every comic in the grid')));
  }

  // The header's Review link (snippets/header.liquid) opens the Review box in place on the stand.
  function wireHeaderReview() {
    var a = D.querySelector('[data-header-review]');
    if (!a) return;
    a.hidden = false; a.setAttribute('data-open-review', ''); a.setAttribute('aria-haspopup', 'dialog');
    a.addEventListener('click', function (e) { e.preventDefault(); closeMenu(); openReviewBox(); });
  }
  function bindPage() {
    wireHeaderReview();
    // One click handler for the whole stand (the dialogs live inside the mount too).
    mount.addEventListener('click', function (e) {
      var t = e.target.closest('button, a');
      if (!t || !mount.contains(t)) return;
      var a = function (n) { return t.getAttribute(n); };
      if (a('data-step') !== null) step(+a('data-step'));
      else if (a('data-tabs') !== null) toggleTabs();
      else if (a('data-tab-index') !== null) { toggleTabs(false); go(+a('data-tab-index'), Math.sign(+a('data-tab-index') - V.i) || 1); }
      else if (a('data-more-photos') !== null) photo(1);
      else if (a('data-photo') !== null) goPhoto(+a('data-photo'));
      else if (a('data-next-box') !== null) { if (V.cb.onNextBox) V.cb.onNextBox(V.cb.next); }
      else if (a('data-goto-index') !== null) go(+a('data-goto-index'), -1);
      else if (a('data-close-viewer') !== null) userClose();
      else if (a('data-review-remove') !== null) removeReview(a('data-review-remove'));
      else if (a('data-list-add') !== null) {
        var b = byId(a('data-id')), list = a('data-list-add');
        if (!b) return;
        if (list === 'cart' && reviewCtx && dlg.contains(t)) enter(b);
        else if (list === 'cart' && inCart(b)) location.href = CFG.cartUrl;
        else if (list === 'review' && hasReview(b.id)) openReviewBox();
        else addTo(list, b);
      }
      else if (a('data-open-review') !== null) { closeMenu(); openReviewBox(); }
      else if (a('data-enter-switch') !== null) { setTarget(L.target === 'cart' ? 'review' : 'cart'); toast(L.target === 'cart' ? 'Enter now adds books straight to your cart' : 'Enter now saves books to your Review box'); }
      else if (a('data-open-guide') !== null) { closeMenu(); showGuide(); }
      else if (a('data-pan') !== null) S.table && S.table.panBy(+a('data-pan') * 3);   // three box columns per press
      else return;
      // After a pointer click in the viewer, hand focus back to the book so Enter/Space mean "add"/"next" again.
      if (e.detail > 0 && V.open && dlg.contains(t) && !t.closest('[data-keep-focus]') && t.isConnected && !otherDialog()) vMain.focus({ preventScroll: true });
    });
    W.addEventListener('keydown', onKey);
    W.addEventListener('pageshow', function (e) { if (e.persisted) refreshCart(); });
    var hd = measureTop();
    if (hd && W.ResizeObserver) new ResizeObserver(function () { measureTop(); }).observe(hd);
  }
  function closeMenu() { try { if (menu.matches(':popover-open')) menu.hidePopover(); } catch (e) { /* no popover API */ } }

  function boot() {
    loadLists();
    buildShell();
    bindPage();
    sync();
    initCart();
    var RSD = W.RawStandData;
    if (!RSD || typeof RSD.load !== 'function') { fail(new Error('The comics list script did not load.')); return; }
    var timer = 0, latest = null;
    var flush = function () { timer = 0; if (latest) { var b = latest; latest = null; apply(b); } };
    var queue = function (books) {                 // the first page draws at once; later pages re-flow at most twice a second
      if (!Array.isArray(books)) return;
      latest = books;
      if (!S.table) flush(); else if (!timer) timer = setTimeout(flush, 450);
    };
    var finish = function (books) {
      clearTimeout(timer); timer = 0; latest = null;
      S.done = true;
      if (books && books !== S.raw) apply(books); else { renderBar(); if (S.pendingRoute) route(); }
    };
    RSD.load({ onProgress: function (books) { queue(books); } }).then(function (r) {
      if (r && Array.isArray(r.books) && !S.table) apply(r.books);
      var done = r && r.done && typeof r.done.then === 'function' ? r.done : Promise.resolve({ books: r && r.books });
      return done.then(function (res) { finish(res && res.books); }, function (err) {
        finish(latest || null);
        toast("Some of the comics couldn't load" + (err && err.message ? ': ' + err.message : '. Reload the page to try again.'), true);
      });
    }, function (err) {
      if (!S.table) fail(err); else finish(null);
    });
  }

  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
})();
